/**
 * Batch H2 — Holder wallet management: add address (unverified), verify by
 * signing a one-time message, set primary, remove, and check NFT ownership.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BrowserProvider } from 'ethers';
import { Wallet, ShieldCheck, ShieldAlert, RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import theme from '../../theme';
import WalletSyncResult from './WalletSyncResult';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = (primary) => ({
  padding: '8px 12px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer',
  border: primary ? 'none' : `1px solid ${theme.colors.borders}`,
  background: primary ? theme.colors.primary : '#fff', color: primary ? '#fff' : theme.colors.text.primary,
  display: 'inline-flex', alignItems: 'center', gap: 6,
});

function WalletHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      <button data-testid="wallet-help-toggle" onClick={() => setOpen(!open)} style={{ ...btn(false), border: 'none', padding: 0, color: theme.colors.primary }}>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} What is a wallet, and do I need one?
      </button>
      {open && (
        <div data-testid="wallet-help" style={{ fontSize: 13, color: theme.colors.text.secondary, marginTop: 8, lineHeight: 1.6 }}>
          <p>A digital wallet (like MetaMask) holds NFTs. Its public address starts with <code>0x</code> and is safe to share.</p>
          <p><b>You don't need one</b> to claim Kickbacks or use benefits the creator gives to your account.</p>
          <p>You need a <b>verified</b> wallet only when a creator makes a benefit available to NFT owners.</p>
          <p><b>Adding an address</b> just saves it (watch-only, shown as Unverified). <b>Verifying</b> means signing a free one-time message in your wallet to prove it's yours. It moves no funds and sends no transaction.</p>
          <p>We will never ask for your private key or recovery phrase. Never share them with anyone.</p>
        </div>
      )}
    </div>
  );
}

export default function WalletManager() {
  const [wallets, setWallets] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const hasProvider = typeof window !== 'undefined' && !!window.ethereum;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/buyer/wallets`, auth);
      setWallets(r.data.wallets || []); setLastSync(r.data.last_sync || null);
    } catch (e) { setError(e?.response?.data?.detail || 'Could not load wallets'); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key); setError(null);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : e?.shortMessage || e?.message || 'Something went wrong');
    } finally { setBusy(null); }
  };

  const add = () => run('add', async () => { await axios.post(`${API}/buyer/wallets`, { address: input.trim() }, auth); setInput(''); }, 'Address saved (unverified)');
  const verify = (address) => run(`verify-${address}`, async () => {
    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const active = (await signer.getAddress()).toLowerCase();
    if (active !== address) throw new Error(`Your wallet app is on ${active.slice(0, 8)}…, not ${address.slice(0, 8)}…. Switch accounts in your wallet and try again.`);
    const n = await axios.post(`${API}/buyer/wallets/nonce`, { address }, auth);
    const signature = await signer.signMessage(n.data.message);
    await axios.post(`${API}/buyer/wallets/verify`, { address, nonce: n.data.nonce, signature }, auth);
  }, 'Wallet verified');
  const connectAndVerify = () => run('connect', async () => {
    const provider = new BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const address = (await signer.getAddress()).toLowerCase();
    await axios.post(`${API}/buyer/wallets`, { address }, auth);
    const n = await axios.post(`${API}/buyer/wallets/nonce`, { address }, auth);
    const signature = await signer.signMessage(n.data.message);
    await axios.post(`${API}/buyer/wallets/verify`, { address, nonce: n.data.nonce, signature }, auth);
  }, 'Wallet connected and verified');
  const remove = (a) => run(`rm-${a}`, () => axios.delete(`${API}/buyer/wallets/${a}`, auth), 'Wallet removed');
  const primary = (a) => run(`pr-${a}`, () => axios.post(`${API}/buyer/wallets/primary`, { address: a }, auth), 'Primary wallet updated');
  const sync = () => run('sync', async () => { const r = await axios.post(`${API}/buyer/wallets/sync`, {}, auth); setLastSync(r.data.sync); }, null);

  return (
    <div data-testid="wallet-manager" id="wallets">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Wallet size={20} color={theme.colors.primary} />
        <h3 style={{ fontSize: 18, fontWeight: 600, color: theme.colors.text.primary }}>Wallets <span style={{ fontSize: 12, color: theme.colors.text.muted, fontWeight: 500 }}>(optional)</span></h3>
      </div>
      <WalletHelp />
      {error && <div data-testid="wallet-error" style={{ padding: 10, borderRadius: 8, background: '#FEF2F2', color: '#991B1B', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
        {wallets.length === 0 && <div data-testid="wallet-empty" style={{ fontSize: 13, color: theme.colors.text.muted }}>No wallet saved. That's fine — your claims and account benefits work without one.</div>}
        {wallets.map(w => (
          <div key={w.address} data-testid={`wallet-row-${w.address}`} style={{ padding: 10, border: `1px solid ${theme.colors.borders}`, borderRadius: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ fontSize: 12, flex: 1, minWidth: 220, wordBreak: 'break-all' }}>{w.address}</code>
            {w.is_primary && <span style={{ fontSize: 10, fontWeight: 800, color: theme.colors.primary }}>PRIMARY</span>}
            <span data-testid={`wallet-status-${w.address}`} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11, fontWeight: 700, color: w.verified ? '#065F46' : '#92400E' }}>
              {w.verified ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />} {w.verified ? 'Verified' : 'Unverified (watch-only)'}
            </span>
            {!w.verified && (
              <button data-testid={`wallet-verify-${w.address}`} style={btn(true)} disabled={!hasProvider || !!busy} onClick={() => verify(w.address)}
                title={hasProvider ? '' : 'Install a browser wallet such as MetaMask to verify'}>
                {busy === `verify-${w.address}` ? 'Waiting for signature…' : 'Verify'}
              </button>
            )}
            {!w.is_primary && <button data-testid={`wallet-primary-${w.address}`} style={btn(false)} disabled={!!busy} onClick={() => primary(w.address)}>Make primary</button>}
            <button data-testid={`wallet-remove-${w.address}`} style={btn(false)} disabled={!!busy} onClick={() => remove(w.address)}><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
      {!hasProvider && wallets.some(w => !w.verified) && (
        <div data-testid="wallet-no-provider" style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 10 }}>
          Verification needs a browser wallet (e.g. MetaMask) on this device. Until then, saved addresses stay Unverified and NFT-only benefits stay locked.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input data-testid="wallet-address-input" value={input} onChange={e => setInput(e.target.value)} placeholder="Public address 0x…"
          style={{ flex: 1, minWidth: 240, padding: '8px 10px', border: `1px solid ${theme.colors.borders}`, borderRadius: 8, fontFamily: 'monospace', fontSize: 13 }} />
        <button data-testid="wallet-add-btn" style={btn(false)} disabled={!input.trim() || !!busy} onClick={add}>Add address</button>
        <button data-testid="wallet-connect-btn" style={btn(true)} disabled={!hasProvider || !!busy} onClick={connectAndVerify}>
          {busy === 'connect' ? 'Waiting for wallet…' : 'Connect & verify wallet'}
        </button>
      </div>

      <div style={{ borderTop: `1px solid ${theme.colors.borders}`, paddingTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button data-testid="wallet-sync-btn" style={btn(false)} disabled={!wallets.length || !!busy} onClick={sync}>
            <RefreshCw size={12} className={busy === 'sync' ? 'animate-spin' : ''} /> {busy === 'sync' ? 'Checking blockchain…' : 'Check my NFTs'}
          </button>
          <span style={{ fontSize: 12, color: theme.colors.text.muted }}>
            <b>Connect & verify</b> proves a wallet is yours. <b>Check my NFTs</b> then reads, on-chain, which of your Kickbacks' NFTs those wallets hold. Neither creates, mints or moves anything.
          </span>
        </div>
        {busy === 'sync' && <div data-testid="wallet-sync-checking" style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#EFF6FF', color: '#1E40AF', fontSize: 13 }}>Checking your wallets against your Kickbacks' NFT contracts…</div>}
        {busy !== 'sync' && <WalletSyncResult sync={lastSync} />}
      </div>
    </div>
  );
}
